import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get } from "@/lib/api";
import { Panel } from "@/components/common";

const t = (iso) => iso?.slice(11, 16);
const STATIONS = [["st_01", "Waterfront 01"], ["st_02", "Waterfront 02"], ["st_03", "Terminal Gate 01"], ["st_04", "Channel East 01"]];

function Chart({ data, lines, unit, testId }) {
  if (!data?.length) return <div className="py-16 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>No snapshots yet — one is stored after every AIS poll.</div>;
  return (
    <div className="h-64" data-testid={testId}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1E3E30" strokeDasharray="3 3" />
          <XAxis dataKey="at" tickFormatter={t} stroke="#64748B" fontSize={11} />
          <YAxis stroke="#64748B" fontSize={11} width={52} />
          <Tooltip contentStyle={{ background: "#11241C", border: "1px solid #1E3E30", fontSize: 12 }} labelFormatter={(l) => `${l?.slice(0, 16)}Z`} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {lines.map(([k, name, c]) => <Line key={k} dataKey={k} name={name} stroke={c} dot={data.length < 10} strokeWidth={2} isAnimationActive={false} />)}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function PortEmissions() {
  const [port, setPort] = useState("tincan");
  const [hours, setHours] = useState(24);
  const [station, setStation] = useState("st_01");
  const { data: ports } = useQuery({ queryKey: ["live-ports", 2], queryFn: () => get("/live/ports", { hours: 2 }) });
  const { data } = useQuery({ queryKey: ["port-emissions", port, hours], queryFn: () => get("/live/ports/emissions", { port_id: port, hours }), refetchInterval: 60000 });
  const { data: samples } = useQuery({ queryKey: ["station-samples", station], queryFn: () => get(`/stations/${station}/samples`) });
  const rows = (data?.items || []).map((r) => ({ ...r, co2_t_h: +(r.co2_kg_h / 1000).toFixed(2) }));
  const sens = {};
  (samples?.items || []).forEach((s) => { (sens[s.observed_at] ||= { at: s.observed_at })[s.pollutant] = s.value; });
  const sensRows = Object.values(sens).sort((a, b) => a.at.localeCompare(b.at));
  const sel = "h-9 rounded border border-[#1E3E30] bg-[#0C1B15] px-2 text-sm text-slate-200";
  return (
    <div className="space-y-4" data-testid="port-emissions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">Port emissions</h1>
          <p className="mt-1 text-sm text-slate-500">Estimated ship emissions per port over time (<span className="text-amber-300">ESTIMATE</span>: {data?.method || "activity-based"}) · next to sensor readings</p></div>
        <div className="flex gap-2">
          <select data-testid="pe-port-select" value={port} onChange={(e) => setPort(e.target.value)} className={sel}>{(ports?.items || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <select data-testid="pe-hours-select" value={hours} onChange={(e) => setHours(Number(e.target.value))} className={sel}>{[6, 12, 24, 48, 168].map((h) => <option key={h} value={h}>last {h} h</option>)}</select>
        </div>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="CO₂ from ships" subtitle="t/h · ESTIMATE" testId="pe-co2-panel"><Chart data={rows} unit=" t/h" lines={[["co2_t_h", "CO₂", "#38BDF8"]]} testId="pe-co2-chart" /></Panel>
        <Panel title="NOₓ, SOₓ, PM from ships" subtitle="kg per hour · ESTIMATE" testId="pe-air-panel"><Chart data={rows} unit=" kg/h" lines={[["nox_kg_h", "NOₓ", "#F97316"], ["sox_kg_h", "SOₓ", "#FACC15"], ["pm_kg_h", "PM", "#A3E635"]]} testId="pe-air-chart" /></Panel>
        <Panel title="Ships in port" subtitle="count at each poll" testId="pe-vessels-panel"><Chart data={rows} unit="" lines={[["vessels", "Ships", "#5EEAD4"]]} testId="pe-vessels-chart" /></Panel>
        <Panel title="Sensor readings" subtitle="µg/m³ · Tin Can DEMO stations · synthetic demo day (2026-10-18), NOT on the same clock as the live ship estimates" testId="pe-sensor-panel">
          <select data-testid="pe-station-select" value={station} onChange={(e) => setStation(e.target.value)} className={`${sel} mb-2 h-8 text-xs`}>{STATIONS.map(([id, n]) => <option key={id} value={id}>{n}</option>)}</select>
          <Chart data={sensRows} unit=" µg/m³" lines={[["NO2", "NO₂", "#F97316"], ["SO2", "SO₂", "#FACC15"], ["PM10", "PM10", "#A3E635"]]} testId="pe-sensor-chart" />
        </Panel>
      </div>
    </div>
  );
}
